"use client";

import { LOGOUT_URL } from "@/constants";
import { User } from "@/types";
import { useQueryClient } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { useGetUser } from "./use-get-user";
interface UseAuthenticationResult {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  logoutUser: () => Promise<void>;
}

// Enable is used to disable authentication check on certain pages
// Eg: We just need to check authen on AuthProvider, for some components or pages that we need to use data from authen only,
// We dont need to enable to call get me again to avoid looping issue when keep calling api failed.
export function useAuth(enabled = true): UseAuthenticationResult {
  const queryClient = useQueryClient();
  const shouldFetchUser = enabled;

  const {
    data: user,
    isFetching: isFetchingUser,
    isLoading,
    error,
    refetch,
  } = useGetUser({
    enabled: enabled,
  });

  // This hook is used to check if user is navigating to another page
  // We need to refetch user data when user navigate to another page
  useCheckAndRefetchGetMeWhenNavigating({
    shouldFetchUser,
    isFetchingUser,
    refetch,
    enabled,
  });
  // Determine authentication status
  const isAuthenticated = !!user && !error;

  const logoutUser = async () => {
    // Clear all local data first
    await queryClient.clear();
    // Then redirect to account app to logout user
    window.location.href = LOGOUT_URL;
  };

  return {
    user: user || null,
    isAuthenticated,
    isLoading,
    error: error?.message || null,
    logoutUser,
  };
}

type UseCheckAndRefetchGetMeWhenNavigating = {
  shouldFetchUser: boolean;
  isFetchingUser: boolean;
  refetch: () => void;
  enabled: boolean;
};
function useCheckAndRefetchGetMeWhenNavigating({
  shouldFetchUser,
  isFetchingUser,
  enabled,
  refetch,
}: UseCheckAndRefetchGetMeWhenNavigating) {
  const pathname = usePathname();

  // Track previous pathname to detect navigation changes
  const prevPathnameRef = useRef(pathname);
  const hasNavigatedRef = useRef(false);

  // Effect to handle navigation and refetch user data only once per navigation
  useEffect(() => {
    // Because we're calling this hook from many places, we need to check if it's enabled
    // Enable is used to call and recall api.
    // That's why we only do logic to refetch for hook with enabled = true
    // It will prevent from calling redundant API get me
    if (!enabled) return;

    // Check if pathname has actually changed
    const hasNavigated = prevPathnameRef.current !== pathname;

    if (hasNavigated) {
      prevPathnameRef.current = pathname;
      hasNavigatedRef.current = true;

      // Determine if we should refetch based on conditions
      const shouldRefetch = shouldFetchUser && !isFetchingUser;
      if (shouldRefetch) {
        refetch();
      }
    }
  }, [pathname, shouldFetchUser, isFetchingUser, refetch, enabled]);

  // Reset navigation flag after user data is fetched
  useEffect(() => {
    if (hasNavigatedRef.current && !isFetchingUser) {
      hasNavigatedRef.current = false;
    }
  }, [isFetchingUser]);
}
